import { getFullTimeFromISOString } from 'pos-shared/utils/dateTimeHelpers.ts';
import { z } from 'zod';

import { DayJsSchema } from './commonSchema.ts';
import { PagedResultSchema } from './pagingSchema.ts';

export const ScheduleLaneSchema = z.object({
  id: z.string(),
  dayOfTheWeek: z.string(),
  dateFrom: z.string(),
  dateTo: z.string(),
});

export const ScheduleLaneFormSchema = z.object({
  dateFrom: DayJsSchema,
  dateTo: DayJsSchema,
  dayOfTheWeek: z.string().or(z.string().array()),
});

export const CreateScheduleLaneSchema = ScheduleLaneSchema.omit({
  id: true,
}).extend({
  dayOfTheWeek: z.string().or(z.array(z.string())),
});

export const ScheduleLaneListSchema = z.array(ScheduleLaneSchema);

export const TradingTimesSchema = z.object({
  id: z.string(),
  dayOfWeek: z.string(),
  opening: z.string(),
  closing: z.string(),
});

export const CreateTradingTimeSchema = TradingTimesSchema.omit({
  id: true,
});

export const TradingTimesFormSchema = z.object({
  dayOfWeek: z.string().min(1),
  opening: DayJsSchema,
  closing: DayJsSchema,
});

export const CreateTradingTimesFESchema = z
  .object({
    dayOfWeek: z.string().nonempty(),
    opening: z.any(),
    closing: z.any(),
  })
  .refine(
    (schema) => {
      const start = getFullTimeFromISOString(schema.opening);
      const end = getFullTimeFromISOString(schema.closing);

      const isValid = start < end;
      return isValid;
    },
    {
      error: 'Closing time cannot be earlier than opening time',
      path: ['closing'],
    }
  );

export const UpdateTradingTimesFESchema = z
  .object({
    id: z.string(),
    dayOfWeek: z.string().min(1),
    opening: z.string(),
    closing: z.string(),
  })
  .refine(
    (schema) => {
      const start = getFullTimeFromISOString(schema.opening);
      const end = getFullTimeFromISOString(schema.closing);

      const isValid = start < end;
      return isValid;
    },
    {
      error: 'Closing time cannot be earlier than opening time',
      path: ['closing'],
    }
  );

export const TradingTimesListSchema = z.array(TradingTimesSchema);

export const NonTradingDatesSchema = z.object({
  id: z.string(),
  date: z.string(),
});

export const CreateNonTradingDateSchema = NonTradingDatesSchema.omit({
  id: true,
});

export const NonTradingDatesListSchema = z.array(NonTradingDatesSchema);

export const DropOffsSchema = z.object({
  id: z.string().optional(),
  time: z.string(),
  length: z.number(),
  dayOfTheWeek: z.string(),
  flexibility: z.enum(['AllDay', 'Am', 'Pm', 'Fixed']),
});

export const DropOffsFormSchema = DropOffsSchema.omit({ time: true }).extend({
  time: DayJsSchema,
});

export const CreateDropOffSchema = DropOffsSchema.omit({
  id: true,
});

export const DropOffsListSchema = z.array(DropOffsSchema);

export const OpeningHoursGroupSchema = z.object({
  id: z.string(),
  code: z.string().max(50).nonempty(),
  groupName: z.string().max(50).nonempty(),
  scheduleLanes: z.array(ScheduleLaneSchema),
  tradingTimes: z.array(TradingTimesSchema),
  nonTradingDates: z.array(NonTradingDatesSchema),
  dropOffs: z.array(DropOffsSchema),
});

export const CreateOpeningHoursGroupSchema = OpeningHoursGroupSchema.omit({
  id: true,
  scheduleLanes: true,
  tradingTimes: true,
  nonTradingDates: true,
  dropOffs: true,
  groupName: true,
  code: true,
}).extend({
  code: z
    .string()
    .min(1, { error: 'Code is required' })
    .max(50)
    .regex(/^[A-Za-z0-9]+$/, 'No non-alphanumeric characters or spaces'),
  groupName: z
    .string()
    .min(1, { error: 'Group Name is required' })
    .max(50)
    .regex(/^[A-Za-z0-9]+$/, 'No non-alphanumeric characters or spaces'),
});

export const UpdateOpeningHoursGroupSchema = OpeningHoursGroupSchema.omit({
  id: true,
  scheduleLanes: true,
  tradingTimes: true,
  nonTradingDates: true,
  dropOffs: true,
}).partial();

export const UpdateOpeningHoursGroupInputSchema = z.object({
  openingHoursId: z.string(),
  data: UpdateOpeningHoursGroupSchema,
});

export const EmptyResponseSchema = z.string().length(0).or(z.undefined());

export const CreatedOpeningHoursResponseSchema = z.object({
  id: z.uuid().optional(),
});

export const ScheduleLaneInputSchema = z.object({
  openingHoursId: z.string(),
  scheduleLaneId: z.string(),
  data: ScheduleLaneSchema.or(CreateScheduleLaneSchema),
});

export const TradingTimesInputSchema = z.object({
  openingHoursId: z.string(),
  tradingTimesId: z.string(),
  data: TradingTimesSchema.or(CreateTradingTimeSchema),
});

export const NonTradingDatesInputSchema = z.object({
  openingHoursId: z.string(),
  nonTradingDatesId: z.string(),
  data: NonTradingDatesSchema.optional(),
});

export const DropOffsInputSchema = z.object({
  openingHoursId: z.string(),
  dropOffsId: z.string(),
  data: DropOffsSchema.optional(),
});

export const OpeningHoursGroupListSchema = PagedResultSchema.extend({
  results: z.array(OpeningHoursGroupSchema),
});

export const OpeningHoursListFilterSchema = z.object({
  id: z.uuid().optional(),
  name: z.string().optional(),
  code: z.string().optional(),
  pageNumber: z.number(),
  resultsPerPage: z.number(),
  orderBy: z.string().optional(),
  sortAscending: z.boolean().optional(),
});

export type OpeningHoursGroupModel = z.infer<typeof OpeningHoursGroupSchema>;
export type OpeningHoursGroupListModel = z.infer<
  typeof OpeningHoursGroupListSchema
>;

export type CreateOpeningHoursGroupModel = z.infer<
  typeof CreateOpeningHoursGroupSchema
>;
export type CreatedOpeningHoursResponseModel = z.infer<
  typeof CreatedOpeningHoursResponseSchema
>;

export type UpdateOpeningHoursGroupModel = z.infer<
  typeof UpdateOpeningHoursGroupSchema
>;
export type UpdateOpeningHoursGroupInputModel = z.infer<
  typeof UpdateOpeningHoursGroupInputSchema
>;

export type CreateTradingTimesFEModel = z.infer<
  typeof CreateTradingTimesFESchema
>;

export type UpdateTradingTimesFEModel = z.infer<
  typeof UpdateTradingTimesFESchema
>;

export type ScheduleLaneModel = z.infer<typeof ScheduleLaneSchema>;
export type CreateScheduleLaneModel = z.infer<typeof CreateScheduleLaneSchema>;
export type ScheduleLaneListModel = z.infer<typeof ScheduleLaneListSchema>;
export type ScheduleLaneInputModel = z.infer<typeof ScheduleLaneInputSchema>;
export type ScheduleLaneFormModel = z.infer<typeof ScheduleLaneFormSchema>;

export type TradingTimesModel = z.infer<typeof TradingTimesSchema>;
export type CreateTradingTimeModel = z.infer<typeof CreateTradingTimeSchema>;
export type TradingTimesListModel = z.infer<typeof TradingTimesListSchema>;
export type TradingTimesInputModel = z.infer<typeof TradingTimesInputSchema>;
export type TradingTimesFormModel = z.infer<typeof TradingTimesFormSchema>;

export type NonTradingDatesModel = z.infer<typeof NonTradingDatesSchema>;
export type CreateNonTradingDateModel = z.infer<
  typeof CreateNonTradingDateSchema
>;
export type NonTradingDatesListModel = z.infer<
  typeof NonTradingDatesListSchema
>;
export type NonTradingDatesInputModel = z.infer<
  typeof NonTradingDatesInputSchema
>;

export type DropOffsModel = z.infer<typeof DropOffsSchema>;
export type DropOffsFormModel = z.infer<typeof DropOffsFormSchema>;
export type CreateDropOffModel = z.infer<typeof CreateDropOffSchema>;
export type DropOffsListModel = z.infer<typeof DropOffsListSchema>;
export type DropOffsInputModel = z.infer<typeof DropOffsInputSchema>;

export type EmptyResponseModel = z.infer<typeof EmptyResponseSchema>;
export type OpeningHoursListFilterModel = z.infer<
  typeof OpeningHoursListFilterSchema
>;
